import type { EngineKind } from '../sim/types.ts';

/**
 * The engines, as numbers.
 *
 * Each engine is two sets of numbers. The mechanical side (EngineSpec) says how
 * the revs behave: idle, redline, gears, how fast it revs, whether it has a
 * turbo. The voice (VoiceParams) says what a firing sounds like and what the
 * engine's signature noises are. The synth itself (engineSynth.ts) has no idea
 * which engine it is playing -- every difference between a rotary and a flat
 * four lives here.
 */

export interface EngineSpec {
  idleRpm: number;
  redlineRpm: number;
  /** Where the fuel cut bounces the revs. */
  limiterRpm: number;
  gears: number;
  /** rpm per second the engine can gain revving freely, and lose off the throttle. */
  revRise: number;
  revFall: number;
  /** 1/s. How fast the turbo spools. 0 = no turbo. */
  spool: number;
  /** Fraction of redline below which the turbo makes no boost. */
  boostThreshold: number;
  /**
   * Sequential twin turbos: the fraction of redline where the second turbo
   * joins the first. Boost sags for a moment at the handover, then the second
   * one comes in with its own, lower whistle. Absent for a single stage.
   */
  secondStage?: number;
}

export interface VoiceParams {
  /** Firing pulses per crank revolution: 2 for a four or a two-rotor, 3 for a six. */
  firesPerRev: number;
  /**
   * Spacing of the firings through one cycle, as multiples of the even
   * interval. Even engines are all 1s. The flat four's unequal-length headers
   * bunch its exhaust pulses, which is where the burble comes from.
   */
  pattern: number[];
  /** Relative loudness of each firing in the cycle. */
  levels: number[];
  /** Hz. The body of each exhaust pulse -- low is deep, high is rasp. */
  resonance: number;
  /** How much the pulse body rises with revs, 0..1. */
  resonanceTrack: number;
  /** Seconds. How long each pulse rings. Short is crisp, long is boomy. */
  pulseDecay: number;
  /** 0..1. Noise mixed into each pulse: raspiness. */
  rasp: number;
  /** 0..1. Random variation between firings. */
  jitter: number;
  /** 0..1. The rotary's lumpy idle: firings bunch into groups at low revs. */
  lope: number;
  /** Hz. How often the groups come round at idle. */
  lopeHz: number;
  /** 0..1. Induction noise under load: the intake howl of a high-revving NA. */
  intake: number;
  /** 0..1. Turbo whistle while on boost. */
  whistle: number;
  /** Hz. Whistle pitch at full boost. */
  whistleHz: number;
  /** 0..1. The "stu-tu-tu" of air fluttering back through the turbo on lift-off. */
  flutter: number;
  /** Hz. Flutter rate as it starts; it slows as it dies away. */
  flutterHz: number;
  /** 0..1. Pops and crackles on the overrun. */
  pops: number;
  /**
   * 0..1. Supercharger whine. The blower is belt-driven off the crank, so
   * unlike a turbo it has no lag and no threshold: its pitch follows the revs
   * exactly, and it is there on and off the throttle, loudest under load.
   */
  blower: number;
  /** Hz. Whine pitch at the redline. */
  blowerHz: number;
  /** 1..6. Saturation: how hard the exhaust note is driven. */
  drive: number;
  /** Overall level. */
  gain: number;
}

export interface EngineProfile {
  kind: EngineKind;
  /** Shown in the sound lab. Descriptive, not a make or model. */
  label: string;
  spec: EngineSpec;
  voice: VoiceParams;
}

export const PROFILES: Record<EngineKind, EngineProfile> = {
  na4: {
    kind: 'na4',
    label: 'High-revving NA four',
    spec: {
      idleRpm: 900,
      redlineRpm: 7800,
      limiterRpm: 8100,
      gears: 5,
      revRise: 14000,
      revFall: 9000,
      spool: 0,
      boostThreshold: 1,
    },
    // Thin and rasping low down, opening into a howl: the pulse body rises
    // steeply with revs, and the intake noise is loud.
    voice: {
      firesPerRev: 2,
      pattern: [1, 1, 1, 1],
      levels: [1, 0.94, 0.98, 0.92],
      resonance: 420,
      resonanceTrack: 0.9,
      pulseDecay: 0.0045,
      rasp: 0.45,
      jitter: 0.12,
      lope: 0,
      lopeHz: 3,
      intake: 0.55,
      whistle: 0,
      whistleHz: 5000,
      flutter: 0,
      flutterHz: 15,
      pops: 0.2,
      blower: 0,
      blowerHz: 3000,
      drive: 2.6,
      gain: 0.9,
    },
  },
  rotary: {
    kind: 'rotary',
    label: 'Two-rotor rotary',
    spec: {
      idleRpm: 950,
      redlineRpm: 8500,
      limiterRpm: 9000,
      gears: 5,
      revRise: 16000,
      revFall: 11000,
      spool: 0,
      boostThreshold: 1,
    },
    // Buzzy and high, with the brap-brap idle: firings bunch into groups a few
    // times a second at low revs, and it pops hard on the overrun.
    voice: {
      firesPerRev: 2,
      pattern: [1, 1],
      levels: [1, 0.9],
      resonance: 560,
      resonanceTrack: 0.6,
      pulseDecay: 0.0035,
      rasp: 0.7,
      jitter: 0.3,
      lope: 0.9,
      lopeHz: 3.4,
      intake: 0.2,
      whistle: 0,
      whistleHz: 5000,
      flutter: 0,
      flutterHz: 15,
      pops: 0.9,
      blower: 0,
      blowerHz: 3000,
      drive: 3.4,
      gain: 1.2,
    },
  },
  turbo6: {
    kind: 'turbo6',
    label: 'Turbo straight six',
    spec: {
      idleRpm: 850,
      redlineRpm: 7600,
      limiterRpm: 8000,
      gears: 6,
      revRise: 11000,
      revFall: 8000,
      spool: 2.2,
      boostThreshold: 0.35,
    },
    // Smooth and deeper: three even firings a revolution, little variation. The
    // character is the turbo -- whistle on boost, flutter on lift-off.
    voice: {
      firesPerRev: 3,
      pattern: [1, 1, 1, 1, 1, 1],
      levels: [1, 0.97, 0.99, 0.96, 1, 0.97],
      resonance: 300,
      resonanceTrack: 0.65,
      pulseDecay: 0.006,
      rasp: 0.25,
      jitter: 0.06,
      lope: 0,
      lopeHz: 3,
      intake: 0.25,
      whistle: 0.8,
      whistleHz: 5400,
      flutter: 1,
      flutterHz: 17,
      pops: 0.3,
      blower: 0,
      blowerHz: 3000,
      drive: 2.2,
      gain: 0.95,
    },
  },
  boxer4: {
    kind: 'boxer4',
    label: 'Turbo flat four',
    spec: {
      idleRpm: 800,
      redlineRpm: 7200,
      limiterRpm: 7600,
      gears: 5,
      revRise: 11000,
      revFall: 7500,
      spool: 1.8,
      boostThreshold: 0.4,
    },
    // Deep and uneven: unequal-length headers bunch the exhaust pulses in
    // pairs, which gives the off-beat burble. A milder turbo than the six.
    voice: {
      firesPerRev: 2,
      pattern: [1.22, 0.78, 1.16, 0.84],
      levels: [1, 0.6, 0.92, 0.66],
      resonance: 230,
      resonanceTrack: 0.5,
      pulseDecay: 0.008,
      rasp: 0.3,
      jitter: 0.1,
      lope: 0.15,
      lopeHz: 2.2,
      intake: 0.2,
      whistle: 0.4,
      whistleHz: 4600,
      flutter: 0.45,
      flutterHz: 13,
      pops: 0.45,
      blower: 0,
      blowerHz: 3000,
      drive: 2.8,
      gain: 1,
    },
  },
  flat6tt: {
    kind: 'flat6tt',
    label: 'Twin-turbo flat six',
    spec: {
      idleRpm: 900,
      redlineRpm: 7500,
      limiterRpm: 7900,
      gears: 6,
      revRise: 12500,
      revFall: 8500,
      spool: 2.8,
      boostThreshold: 0.3,
    },
    // Even-firing like the straight six, but harder and more metallic: a
    // higher, rasping pulse body and more induction noise. Two small turbos
    // spool quickly, so the whistle arrives early and the flutter is lighter.
    voice: {
      firesPerRev: 3,
      pattern: [1, 1, 1, 1, 1, 1],
      levels: [1, 0.95, 0.98, 0.94, 1, 0.95],
      resonance: 360,
      resonanceTrack: 0.8,
      pulseDecay: 0.005,
      rasp: 0.42,
      jitter: 0.07,
      lope: 0,
      lopeHz: 3,
      intake: 0.4,
      whistle: 0.55,
      whistleHz: 6200,
      flutter: 0.55,
      flutterHz: 20,
      pops: 0.4,
      blower: 0,
      blowerHz: 3000,
      drive: 2.6,
      gain: 0.95,
    },
  },
  turbo4: {
    kind: 'turbo4',
    label: 'Turbo inline four',
    spec: {
      idleRpm: 850,
      redlineRpm: 7200,
      limiterRpm: 7600,
      gears: 6,
      revRise: 12000,
      revFall: 8500,
      spool: 2,
      boostThreshold: 0.38,
    },
    // An even four with a turbo muffling its rasp: deeper than the NA four,
    // with a strong whistle and a busy flutter on every lift.
    voice: {
      firesPerRev: 2,
      pattern: [1, 1, 1, 1],
      levels: [1, 0.95, 0.98, 0.93],
      resonance: 320,
      resonanceTrack: 0.7,
      pulseDecay: 0.0055,
      rasp: 0.32,
      jitter: 0.1,
      lope: 0,
      lopeHz: 3,
      intake: 0.25,
      whistle: 0.75,
      whistleHz: 5200,
      flutter: 0.9,
      flutterHz: 19,
      pops: 0.4,
      blower: 0,
      blowerHz: 3000,
      drive: 2.5,
      gain: 0.95,
    },
  },
  scv8: {
    kind: 'scv8',
    label: 'Supercharged V8',
    spec: {
      idleRpm: 750,
      redlineRpm: 6300,
      limiterRpm: 6600,
      gears: 6,
      // A big heavy crank: it revs lazily and falls slowly.
      revRise: 8500,
      revFall: 6000,
      // No turbo. The supercharger's boost is instant; its sound is the whine.
      spool: 0,
      boostThreshold: 1,
    },
    // Deep and loping: four firings a revolution from a cross-plane crank,
    // whose two banks fire unevenly -- the V8 burble. Long, boomy pulses,
    // driven hard, and over the top of it all the blower's whine, climbing
    // with the revs.
    voice: {
      firesPerRev: 4,
      pattern: [1.12, 0.88, 1.06, 0.94, 1.1, 0.9, 0.96, 1.04],
      levels: [1, 0.72, 0.9, 0.78, 0.98, 0.7, 0.86, 0.8],
      resonance: 170,
      resonanceTrack: 0.55,
      pulseDecay: 0.009,
      rasp: 0.3,
      jitter: 0.1,
      lope: 0.12,
      lopeHz: 2,
      intake: 0.3,
      whistle: 0,
      whistleHz: 5000,
      flutter: 0,
      flutterHz: 15,
      pops: 0.6,
      blower: 0.8,
      blowerHz: 3000,
      drive: 3.2,
      gain: 1,
    },
  },
  race4: {
    kind: 'race4',
    label: 'Race turbo four (anti-lag)',
    // The 3S-GTE in the 1997 TOM'S GT500: a 2-litre turbo four built to race,
    // about 490hp through an air restrictor. Light internals rev it hard and
    // fast; anti-lag keeps the turbo lit off the throttle, so it spools almost
    // at once and bangs and crackles on every lift.
    spec: {
      idleRpm: 1200,
      redlineRpm: 8800,
      limiterRpm: 9000,
      gears: 6,
      revRise: 18000,
      revFall: 12000,
      spool: 3.5,
      boostThreshold: 0.25,
    },
    // Short, hard, rasping pulses through an open race exhaust, driven harder
    // than any road car here; a high turbo whistle; and the anti-lag's bangs as
    // the loudest overrun in the game. The flutter is light: a race car dumps
    // its boost through a valve rather than back through the turbo.
    voice: {
      firesPerRev: 2,
      pattern: [1, 1, 1, 1],
      levels: [1, 0.93, 0.97, 0.9],
      resonance: 460,
      resonanceTrack: 0.85,
      pulseDecay: 0.0032,
      rasp: 0.62,
      jitter: 0.14,
      lope: 0,
      lopeHz: 3,
      intake: 0.35,
      whistle: 0.6,
      whistleHz: 6800,
      flutter: 0.35,
      flutterHz: 24,
      pops: 1.3,
      blower: 0,
      blowerHz: 3000,
      drive: 4.2,
      gain: 1.05,
    },
  },
  bigturbo6: {
    kind: 'bigturbo6',
    label: 'Big single-turbo straight six',
    // A 2JZ-GTE converted from its two small turbos to one big one, as the
    // film's orange Supra was said to be. Smooth and deep like any straight
    // six, but the big turbo is lazy: nothing much below half revs, then it
    // comes on hard. That lag and surge is the car's character.
    spec: {
      idleRpm: 850,
      redlineRpm: 7200,
      limiterRpm: 7500,
      gears: 6,
      revRise: 10000,
      revFall: 7500,
      spool: 1.3,
      boostThreshold: 0.5,
    },
    // Three even firings a revolution, smooth and rounder than the RB26's; the
    // whistle is a big turbo's, lower and louder than any other here, and the
    // flutter on lift-off is the loudest and slowest -- the classic
    // "stu-tu-tu" of a big single turbo.
    voice: {
      firesPerRev: 3,
      pattern: [1, 1, 1, 1, 1, 1],
      levels: [1, 0.96, 0.99, 0.95, 1, 0.96],
      resonance: 280,
      resonanceTrack: 0.65,
      pulseDecay: 0.0065,
      rasp: 0.28,
      jitter: 0.07,
      lope: 0,
      lopeHz: 3,
      intake: 0.3,
      whistle: 1,
      whistleHz: 4300,
      flutter: 1.35,
      flutterHz: 13,
      pops: 0.5,
      blower: 0,
      blowerHz: 3000,
      drive: 2.6,
      gain: 1,
    },
  },
  mturbo6: {
    kind: 'mturbo6',
    label: 'Modern twin-turbo straight six',
    // BMW's S58 in the AC Schnitzer M4: a 3-litre straight six on two small
    // turbos, tuned to about 600hp. Modern turbos are quick and quiet, so boost
    // is there almost from idle and the whistle is faint; the voice is the
    // sports exhaust.
    spec: {
      idleRpm: 750,
      redlineRpm: 7200,
      limiterRpm: 7400,
      gears: 8,
      revRise: 11500,
      revFall: 8500,
      spool: 3,
      boostThreshold: 0.22,
    },
    // Deeper and harder-edged than the RB26: a lower pulse body driven harder,
    // with more rasp. No flutter -- the valves recirculate the air -- and the
    // sports exhaust's crackle on every lift is the loudest thing about it.
    voice: {
      firesPerRev: 3,
      pattern: [1, 1, 1, 1, 1, 1],
      levels: [1, 0.97, 0.99, 0.96, 1, 0.97],
      resonance: 250,
      resonanceTrack: 0.7,
      pulseDecay: 0.0055,
      rasp: 0.42,
      jitter: 0.06,
      lope: 0,
      lopeHz: 3,
      intake: 0.3,
      whistle: 0.25,
      whistleHz: 6400,
      flutter: 0,
      flutterHz: 15,
      pops: 1.05,
      blower: 0,
      blowerHz: 3000,
      drive: 3.1,
      gain: 1,
    },
  },
  seqrotary: {
    kind: 'seqrotary',
    label: 'Sequential twin-turbo rotary',
    // The FD RX-7's 13B-REW: the same two rotors as the FC, on two turbos in
    // sequence. The first works alone low down; at about 4,000rpm the second
    // joins, with a dip in boost at the handover and then a second surge. Street
    // tuned: more boost, a louder exhaust and a blow-off valve.
    spec: {
      idleRpm: 900,
      redlineRpm: 8000,
      limiterRpm: 8400,
      gears: 5,
      revRise: 15000,
      revFall: 11000,
      spool: 2.6,
      boostThreshold: 0.22,
      secondStage: 0.5,
    },
    // The rotary's buzz and brap-brap idle, slightly less lumpy than the FC's
    // street port, with the turbos over it: a whistle on each stage, a chatter
    // on lift-off, and the rotary's hard pops on the overrun.
    voice: {
      firesPerRev: 2,
      pattern: [1, 1],
      levels: [1, 0.9],
      resonance: 520,
      resonanceTrack: 0.6,
      pulseDecay: 0.004,
      rasp: 0.62,
      jitter: 0.26,
      lope: 0.75,
      lopeHz: 3.2,
      intake: 0.2,
      whistle: 0.7,
      whistleHz: 5800,
      flutter: 0.6,
      flutterHz: 21,
      pops: 1.1,
      blower: 0,
      blowerHz: 3000,
      drive: 3.4,
      gain: 1.15,
    },
  },
  racev6: {
    kind: 'racev6',
    label: 'Race V6, naturally aspirated',
    // The ARTA NSX's C32B in 2005 GT500 trim: a 3.5-litre V6, no turbo, built to
    // rev. It answers the throttle at once and climbs to 9,500rpm; the scream
    // at the top is the car.
    spec: {
      idleRpm: 1300,
      redlineRpm: 9500,
      limiterRpm: 9700,
      gears: 6,
      revRise: 20000,
      revFall: 13000,
      spool: 0,
      boostThreshold: 1,
    },
    // Three even firings a revolution, like a six, but short and hard through
    // an open race exhaust; the pulse body climbs steeply with revs and the
    // intake roars, which together make the howl. Fewer bangs than the Tom's:
    // no anti-lag, just crackle on the overrun.
    voice: {
      firesPerRev: 3,
      pattern: [1, 1, 1, 1, 1, 1],
      levels: [1, 0.92, 0.97, 0.9, 0.98, 0.93],
      resonance: 520,
      resonanceTrack: 1,
      pulseDecay: 0.003,
      rasp: 0.55,
      jitter: 0.12,
      lope: 0,
      lopeHz: 3,
      intake: 0.8,
      whistle: 0,
      whistleHz: 5000,
      flutter: 0,
      flutterHz: 15,
      pops: 0.6,
      blower: 0,
      blowerHz: 3000,
      drive: 3.8,
      gain: 1.05,
    },
  },
};

export const ENGINE_KINDS: EngineKind[] = [
  'na4',
  'rotary',
  'turbo6',
  'boxer4',
  'flat6tt',
  'turbo4',
  'scv8',
  'race4',
  'bigturbo6',
  'mturbo6',
  'seqrotary',
  'racev6',
];

/** The voice numbers the sound lab exposes as sliders. */
export interface VoiceSpec {
  key: keyof VoiceParams;
  label: string;
  help: string;
  min: number;
  max: number;
  step: number;
}

export const VOICE_SPECS: VoiceSpec[] = [
  { key: 'resonance', label: 'Pitch of the note', help: 'Low is deep and boomy, high is thin and rasping.', min: 120, max: 900, step: 10 },
  { key: 'resonanceTrack', label: 'Pitch rise with revs', help: 'How much the note climbs as the revs go up. High gives a howl.', min: 0, max: 1.5, step: 0.05 },
  { key: 'pulseDecay', label: 'Pulse length', help: 'Short is crisp and tight, long is boomy.', min: 0.001, max: 0.02, step: 0.0005 },
  { key: 'rasp', label: 'Rasp', help: 'Grit in every firing.', min: 0, max: 1, step: 0.05 },
  { key: 'drive', label: 'Exhaust drive', help: 'How hard the note is pushed. High is angrier.', min: 1, max: 6, step: 0.1 },
  { key: 'jitter', label: 'Roughness', help: 'Variation between firings. Zero is perfectly smooth.', min: 0, max: 0.6, step: 0.02 },
  { key: 'lope', label: 'Idle lope', help: 'The rotary brap: firings bunched into groups at low revs.', min: 0, max: 1, step: 0.05 },
  { key: 'lopeHz', label: 'Lope speed', help: 'How many groups a second at idle.', min: 1, max: 8, step: 0.1 },
  { key: 'intake', label: 'Intake howl', help: 'Induction noise under load.', min: 0, max: 1, step: 0.05 },
  { key: 'whistle', label: 'Turbo whistle', help: 'Whistle on boost.', min: 0, max: 1, step: 0.05 },
  { key: 'flutter', label: 'Turbo flutter', help: 'The stu-tu-tu on lift-off.', min: 0, max: 1.5, step: 0.05 },
  { key: 'flutterHz', label: 'Flutter speed', help: 'How fast the flutter chatters.', min: 6, max: 30, step: 0.5 },
  { key: 'pops', label: 'Pops and crackles', help: 'On the overrun, off the throttle.', min: 0, max: 1.5, step: 0.05 },
  { key: 'blower', label: 'Supercharger whine', help: 'The blower: follows the revs with no lag, loudest under load.', min: 0, max: 1.5, step: 0.05 },
  { key: 'blowerHz', label: 'Whine pitch', help: 'How high the whine climbs at the redline.', min: 800, max: 6000, step: 50 },
  { key: 'gain', label: 'Volume', help: 'This engine only.', min: 0.2, max: 2, step: 0.05 },
];

// --- Local overrides from the sound lab ----------------------------------------

const STORAGE_KEY = 'idrift.sound.v1';
const SHIPPED: Record<EngineKind, VoiceParams> = {
  na4: { ...PROFILES.na4.voice },
  rotary: { ...PROFILES.rotary.voice },
  turbo6: { ...PROFILES.turbo6.voice },
  boxer4: { ...PROFILES.boxer4.voice },
  flat6tt: { ...PROFILES.flat6tt.voice },
  turbo4: { ...PROFILES.turbo4.voice },
  scv8: { ...PROFILES.scv8.voice },
  race4: { ...PROFILES.race4.voice },
  bigturbo6: { ...PROFILES.bigturbo6.voice },
  mturbo6: { ...PROFILES.mturbo6.voice },
  seqrotary: { ...PROFILES.seqrotary.voice },
  racev6: { ...PROFILES.racev6.voice },
};

type Overrides = Partial<Record<EngineKind, Partial<Record<keyof VoiceParams, number>>>>;

function read(): Overrides {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Overrides) : {};
  } catch {
    return {};
  }
}

function write(o: Overrides): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
  } catch {
    // Not remembered; applies for this session.
  }
}

/**
 * Apply what was changed in the sound lab. Sound never touches the sim, so
 * unlike handling tuning this applies whether or not tuning mode is on.
 */
export function applySoundOverrides(): void {
  const o = read();
  for (const kind of ENGINE_KINDS) {
    const stored = o[kind];
    if (!stored) continue;
    for (const spec of VOICE_SPECS) {
      const v = stored[spec.key];
      if (typeof v === 'number' && Number.isFinite(v)) (PROFILES[kind].voice[spec.key] as number) = v;
    }
  }
}

export function setVoice(kind: EngineKind, key: keyof VoiceParams, value: number): void {
  (PROFILES[kind].voice[key] as number) = value;
  const o = read();
  o[kind] = { ...(o[kind] ?? {}), [key]: value };
  write(o);
}

export function resetVoice(kind: EngineKind): void {
  PROFILES[kind].voice = { ...SHIPPED[kind] };
  const o = read();
  delete o[kind];
  write(o);
}

export function describeVoice(kind: EngineKind): string {
  const out: Record<string, number> = {};
  for (const spec of VOICE_SPECS) out[spec.key] = Number((PROFILES[kind].voice[spec.key] as number).toFixed(4));
  return JSON.stringify({ engine: kind, voice: out }, null, 2);
}
